'use client'

import React, { ChangeEventHandler, useState } from "react";
import { DefinitelyOurButton } from "../definitelyOurButton";

interface Checkbox {
  label: string;
  checked: boolean;
}

type ContactUsSentStatus = "none" | "sending" | "done";

export const ContactUsForm: React.FC = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<ContactUsSentStatus>("none");
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const [checkboxes, setCheckboxes] = useState<Checkbox[]>([
    { label: "SEO", checked: false },
    { label: "Web Design", checked: false },
    { label: "Web Developing", checked: false },
    { label: "Logo", checked: false },
    { label: "Marketing", checked: false },
    { label: "Mobile App", checked: false },
    { label: "Subscriptions", checked: false },
    { label: "Other", checked: false },
  ]);

  const handleCheckboxChange = (index: number) => {
    const updatedCheckboxes = [...checkboxes];
    updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;
    setCheckboxes(updatedCheckboxes);
    setErrorMessage(null);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    const isAtLeastOneCheckboxSelected = checkboxes.some((box) => box.checked);
    if (!isAtLeastOneCheckboxSelected) {
      setErrorMessage("Please select at least one service option.");
      return;
    }

    setStatus("sending");

    try {
      const res = await fetch("/submitTicket", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          tags: checkboxes.filter((box) => box.checked).map((it) => it.label),
        }),
      });

      if (res.ok) {
        setShowThankYouMessage(true);
        setTimeout(() => {
          setShowThankYouMessage(false);
        }, 4000);
      } else {
        console.error("Error submitting form");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setStatus("done");
    }
  };

  const isAtLeastOneCheckboxSelected = checkboxes.some((box) => box.checked);

  return (
    <>
      {showThankYouMessage && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#3B82F6",
            color: "white",
            padding: "10px 15px",
            borderRadius: "10px",
            zIndex: 1000,
            animation: "fadeOut 5s forwards",
          }}
        >
          Thank you for your message!
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="w-full items-center flex justify-center my-12">
          <div className="flex flex-col gap-6 p-6 flex-grow max-w-[500px] blue-stroke rounded-[25px]">
            <h3 className="text-center">Contact Form</h3>

            <div className="flex flex-col gap-4 w-full">
              <div className="flex justify-between gap-4">
                <div className="w-full">
                  <div className="flex gap-2">
                    <label className="">First Name</label>
                    <label className="text-[#FF0000] opacity-70">*</label>
                  </div>
                  <input
                    required
                    type="text"
                    name="name"
                    className="blue-border w-full p-2 rounded-[15px]"
                    onChange={handleChange}
                  />
                </div>
                <div className="w-full">
                  <div className="flex gap-2">
                    <label className="">Last Name</label>
                    <label className="text-[#FF0000] opacity-70">*</label>
                  </div>
                  <input
                    required
                    name="second_name"
                    type="text"
                    className="blue-border w-full p-2 rounded-[15px]"
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <div className="flex gap-2">
                  <label className="">Email</label>
                  <label className="text-[#FF0000] opacity-70">*</label>
                </div>
                <input
                  required
                  name="email"
                  type="email"
                  className="blue-border p-2 rounded-[15px] w-full"
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <p>Service inquire</p>
                <p className="text-[#FF0000] opacity-70">
                  * (at least 1 option)
                </p>
              </div>

              <div className="flex gap-2 flex-wrap">
                <div className="flex gap-3 flex-wrap group-hover:text-blue-600">
                  {checkboxes.map((option, index) => (
                    <label
                      key={index}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={option.checked}
                        className={`appearance-none w-4 h-4 border border-gray-400 rounded-md
                        ${
                          option.checked
                            ? "bg-blue-400 border-white border"
                            : "bg-white"
                        }
                        focus:outline-none focus:ring-2 focus:ring-blue-300
                        transition-all duration-200 ease-in-out transform`}
                        onChange={() => handleCheckboxChange(index)}
                      />
                      <span className="peer-hover:text-blue-600 group-hover:text-blue-600 transition-colors duration-200 ease-in-out">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {errorMessage && (
                <p className="text-red-500 text-sm">{errorMessage}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <label className="">Message</label>
                <label className="text-[#FF0000] opacity-70">*</label>
              </div>
              <textarea
                required
                name="message"
                className="p-2 rounded-[15px] blue-border h-32"
                onChange={handleChange}
              />
            </div>
            <div className="flex justify-center">
              <ButtonBasedOnStatus
                status={status}
                disabled={!isAtLeastOneCheckboxSelected}
              />
            </div>
          </div>
        </div>
      </form>

      <style>
        {`
          @keyframes fadeOut {
            0% {
              opacity: 1;
            }
            90% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              visibility: hidden;
            }
          }
        `}
      </style>
    </>
  );
};

function ButtonBasedOnStatus({
  status,
  disabled,
}: {
  status: ContactUsSentStatus;
  disabled?: boolean;
}) {
  if (status === "sending") {
    return <>submiting...</>;
  } else {
    return (
      <DefinitelyOurButton
        type="submit"
        textForButton="Send a Message"
        value=""
        theme="blue"
      />
    );
  }
}